import { CACHE_BUSTER_PARAM, vehicleImageCacheBuster } from './cache-buster';
import { computeCropBox, formatCropParam } from './crop';
import { VEX_GATEWAY_CDN_URL, VEX_STOCK_IMAGES_CDN_URL } from './hosts';
import { withImageParams } from './image-params';
import { toStockNumber } from './stock-number';
import { SpinFrame, VehicleImageVariant } from './types';

const toPathSegment = (value?: number | string | null): string | null => {
  const text = String(value ?? '').trim();

  return text ? encodeURIComponent(text.toUpperCase()) : null;
};

interface SpinFrameImageOptions {
  aspect: number;
  width: number;
  pad?: number;
}

/**
 * `null` when no crop could be computed. The raw frame is a dome shot that is mostly floor and
 * sky, so a caller that `object-fit`s it into a wide box clips the roof and the wheels — the very
 * failure the explicit crop exists to prevent. Callers fall through to their own ladder instead.
 */
export const buildSpinFrameImageUrl = (
  frame: SpinFrame,
  { aspect, width, pad }: SpinFrameImageOptions
): string | null => {
  const crop = computeCropBox({
    boundingBox: frame.boundingBox,
    source: frame.source,
    aspect,
    pad
  });

  if (!crop) return null;

  return withImageParams(frame.url, {
    crop: formatCropParam(crop),
    width
  });
};

interface StockHeroImageOptions {
  stockNumber?: number | string | null;
  width: number;
}

/**
 * Never read the BFF's image URL fields instead: they can disagree with `stockNumber` and point
 * at a different car — seen on a Slate payload whose `primaryImageUrl` was stock 2003921744
 * while `stockNumber` said 2005061569.
 */
export const buildStockHeroImageUrl = ({
  stockNumber,
  width
}: StockHeroImageOptions): string | null => {
  const stock = toStockNumber(stockNumber);

  if (!stock || !VEX_GATEWAY_CDN_URL) return null;

  return withImageParams(`${VEX_GATEWAY_CDN_URL}/${stock}/hero.jpg`, {
    width,
    [CACHE_BUSTER_PARAM]: vehicleImageCacheBuster()
  });
};

const LEGACY_VARIANTS: Record<VehicleImageVariant, string> = {
  desktop: 'desktop_1920x1080',
  mobile: 'mobile_640x640'
};

export type StockPhotoNaming = 'indexed' | 'legacy';

/** BlueSteel requires a colour and the BFF sends none. One of the eight that publish; the rest 404. */
const FIXED_EXTERIOR_COLOR = 'BLACK';

interface StockPhotoImageOptions {
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  variant: VehicleImageVariant;
  naming?: StockPhotoNaming;
  width?: number;
}

/**
 * Every segment is required and the path is case-sensitive — dropping the colour or the trim, or
 * reducing to year/make/model, all 404. `trim` must be the long form (`2.5 SR Sedan 4D`, not
 * `SR`). No paint code is appended: one that disagrees with the colour 404s.
 */
export const buildStockPhotoImageUrl = ({
  year,
  make,
  model,
  trim,
  variant,
  naming = 'indexed',
  width
}: StockPhotoImageOptions): string | null => {
  if (!VEX_STOCK_IMAGES_CDN_URL) return null;

  const required = [year, make, model, trim].map(toPathSegment);

  if (required.some(segment => !segment)) return null;

  const name = [...required, FIXED_EXTERIOR_COLOR].join('_');

  const suffix = naming === 'legacy' ? `stock_${LEGACY_VARIANTS[variant]}` : `stock_1_${variant}`;

  return withImageParams(`${VEX_STOCK_IMAGES_CDN_URL}/stockimages/${name}_${suffix}.png`, {
    width
  });
};

/** Published only under the legacy naming; the `stock_{index}_{variant}` form 404s for it. */
export const buildGenericVehicleImageUrl = ({
  variant,
  width
}: {
  variant: VehicleImageVariant;
  width?: number;
}): string | null => {
  if (!VEX_STOCK_IMAGES_CDN_URL) return null;

  return withImageParams(
    `${VEX_STOCK_IMAGES_CDN_URL}/stockimages/generic_vehicle_${LEGACY_VARIANTS[variant]}.png`,
    { width }
  );
};
